import { useEffect, useRef } from 'react';

// Finger-tracking pager for the tab panels. The panels sit side by side in a
// track; while a horizontal drag is live the track follows the finger, and on
// release it settles on whichever panel won — the next one if the drag went
// far or fast enough, otherwise back where it started.
//
// The viewport carries `touch-action: pan-y`, so the browser keeps vertical
// scrolling for itself and hands horizontal movement to us. That split is
// what lets the hook work without ever calling preventDefault (React's root
// touch listeners are passive, so it couldn't anyway).
//
// React only ever renders the settled position. While the finger is down,
// frames are written straight to the track node inside requestAnimationFrame:
// routing a 60Hz gesture through state would re-render every mounted feed on
// every move, which is exactly the jank this exists to avoid. React never
// fights the imperative writes because its own style values are unchanged
// while a drag is live, so it skips the DOM write on any incidental render.

// How far a touch may wander before we commit it to an axis; a drag assigned
// to the vertical axis is scrolling, and the pager ignores the rest of it.
const AXIS_SLOP = 8;
// A release within FLICK_MS having travelled FLICK_DISTANCE is a flick and
// changes page regardless of how far the track has moved...
const FLICK_MS = 300;
const FLICK_DISTANCE = 50;
// ...otherwise the drag must cover this share of the viewport to commit.
const SETTLE_FRACTION = 0.35;
// Dragging past the first or last panel moves at a fraction of the finger:
// the iOS-style hint that there is nothing further.
const EDGE_RESISTANCE = 3;

const SETTLE_TRANSITION = 'transform 300ms cubic-bezier(0.2, 0, 0, 1)';

// translateX percentages resolve against the track's own width, and the
// track is one viewport wide (a flex row does not grow to fit overflowing
// panels) — so 100% is exactly one panel, whatever `count` is.
const settledTransform = (index) => `translateX(${index * -100}%)`;

export function useTabPager({ count, index, onChange }) {
  const viewportRef = useRef(null);
  const trackRef = useRef(null);
  const gestureRef = useRef(null);
  const frameRef = useRef(0);

  const cancelFrame = () => {
    cancelAnimationFrame(frameRef.current);
    frameRef.current = 0;
  };

  useEffect(() => cancelFrame, []);

  const onTouchStart = (event) => {
    // Anything marked data-pager-scroll scrolls on its own horizontal axis
    // (the columnist rail); gestures starting inside it belong to the
    // browser, not the pager.
    if (event.target.closest('[data-pager-scroll]')) {
      gestureRef.current = null;
      return;
    }

    // A second finger is a pinch: drop the gesture and let the release
    // handler settle whatever the track was doing.
    if (event.touches.length !== 1) {
      gestureRef.current = null;
      return;
    }

    const { clientX, clientY } = event.touches[0];
    gestureRef.current = {
      x: clientX,
      y: clientY,
      startedAt: performance.now(),
      width: viewportRef.current?.clientWidth || window.innerWidth,
      axis: null,
      offset: 0,
    };
  };

  const onTouchMove = (event) => {
    const gesture = gestureRef.current;
    if (!gesture) return;

    const { clientX, clientY } = event.touches[0];
    const dx = clientX - gesture.x;
    const dy = clientY - gesture.y;

    // First movement past the slop claims the gesture for one axis; a
    // diagonal drag never flips between scrolling and paging mid-flight.
    if (!gesture.axis) {
      if (Math.abs(dx) < AXIS_SLOP && Math.abs(dy) < AXIS_SLOP) return;
      gesture.axis = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';
    }
    if (gesture.axis !== 'x') return;

    const pastEdge =
      (index === 0 && dx > 0) || (index === count - 1 && dx < 0);
    gesture.offset = pastEdge ? dx / EDGE_RESISTANCE : dx;

    // Coalesce moves into one style write per frame.
    if (!frameRef.current) {
      frameRef.current = requestAnimationFrame(() => {
        frameRef.current = 0;
        const track = trackRef.current;
        const live = gestureRef.current;
        if (!track || !live) return;

        track.style.transition = 'none';
        track.style.transform = `translateX(calc(${index * -100}% + ${live.offset}px))`;
      });
    }
  };

  // Glide from wherever the drag left the track to a settled panel. Done
  // imperatively even for a snap-back, where React would bail out of the
  // style write (old and new index being equal) and leave the track hanging.
  const settle = (toIndex) => {
    cancelFrame();
    const track = trackRef.current;
    if (track) {
      track.style.transition = SETTLE_TRANSITION;
      track.style.transform = settledTransform(toIndex);
    }
    if (toIndex !== index) onChange(toIndex);
  };

  const onTouchEnd = () => {
    const gesture = gestureRef.current;
    gestureRef.current = null;
    if (!gesture || gesture.axis !== 'x') return;

    const { offset, width, startedAt } = gesture;
    const isFlick =
      performance.now() - startedAt < FLICK_MS &&
      Math.abs(offset) > FLICK_DISTANCE;
    const isFar = Math.abs(offset) > width * SETTLE_FRACTION;

    const next = index + (offset < 0 ? 1 : -1);
    const commit = (isFlick || isFar) && next >= 0 && next < count;
    settle(commit ? next : index);
  };

  const onTouchCancel = () => {
    gestureRef.current = null;
    settle(index);
  };

  return {
    viewportRef,
    trackRef,
    // The settled state React renders; mid-drag frames override it directly
    // on the node, and land back on exactly these values.
    trackStyle: {
      transform: settledTransform(index),
      transition: SETTLE_TRANSITION,
    },
    handlers: { onTouchStart, onTouchMove, onTouchEnd, onTouchCancel },
  };
}
